/**
 * Collections — `/finance/collections`.
 *
 * Chasing what is owed: the escalation ladder, the sweep that walks it, and
 * the periodic statement sent to every customer with an open balance.
 *
 * Three facts the screen states rather than hides.
 *
 * Saving the ladder RECONCILES it. A level removed here is deactivated, not
 * deleted, because a dunning event written last year names a level that must
 * still be explicable — so the screen says "deactivated" rather than pretending
 * the rung never existed.
 *
 * The sweep is structurally idempotent: `(invoice, level)` is unique, so a
 * second run on the same day writes nothing and therefore sends nothing. The
 * screen reports it as accepted rather than as "sent", because what was sent is
 * the job's business and a rerun sending nothing is success, not failure.
 *
 * A statement run is queued, not rendered inline — hundreds of statements would
 * hold a request open for minutes. The run row is polled and its counts shown
 * as they fill in.
 */
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Mail, Plus, Send, Trash2 } from "lucide-react";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Checkbox,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@erp/ui";

import { newIdempotencyKey } from "@/features/shared/ops-api";

import {
  DUNNING_TEMPLATES,
  getCustomerStatementRun,
  ladderEscalates,
  listDunningLevels,
  replaceDunningLevels,
  runCustomerStatements,
  runDunning,
  type CustomerStatementRun,
  type DunningLevel,
} from "../collections-api";

const LEVELS_KEY = ["finance/dunning-levels"];

interface LevelDraft {
  level: string;
  name: string;
  min_days_overdue: string;
  min_open_amount: string;
  notification_template_code: string;
  active: boolean;
}

const toDraft = (level: DunningLevel): LevelDraft => ({
  level: String(level.level),
  name: level.name,
  min_days_overdue: String(level.min_days_overdue),
  min_open_amount: level.min_open_amount,
  notification_template_code: level.notification_template_code,
  active: level.active,
});

export interface CollectionsPageProps {
  canConfigure?: boolean;
  canRunDunning?: boolean;
  canRunStatements?: boolean;
  canReadStatements?: boolean;
  baseCurrency?: string;
}

export function CollectionsPage({
  canConfigure = false,
  canRunDunning = false,
  canRunStatements = false,
  canReadStatements = false,
  baseCurrency = "SAR",
}: CollectionsPageProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [drafts, setDrafts] = useState<LevelDraft[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<string | null>(null);
  const [statementRunId, setStatementRunId] = useState<string | null>(null);
  const [statementForm, setStatementForm] = useState({ as_of: "", currency: baseCurrency });
  const [sweepAsOf, setSweepAsOf] = useState("");

  const levels = useQuery({
    queryKey: LEVELS_KEY,
    queryFn: async ({ signal }) => [...(await listDunningLevels(signal)).items],
  });

  useEffect(() => {
    if (levels.data !== undefined && drafts === null) {
      setDrafts(
        [...levels.data]
          .sort((left, right) => left.level - right.level)
          .map((level) => toDraft(level)),
      );
    }
  }, [levels.data, drafts]);

  const run = useQuery<CustomerStatementRun>({
    queryKey: ["finance/customer-statement-run", statementRunId],
    queryFn: ({ signal }) => getCustomerStatementRun(statementRunId as string, signal),
    enabled: statementRunId !== null && canReadStatements,
    // The job fills the counts in as it works, so the row is polled until it
    // stops moving.
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === "completed" || status === "failed" ? false : 1500;
    },
  });

  const ladder = useMemo(
    () =>
      (drafts ?? []).map((draft) => ({
        min_days_overdue: Number(draft.min_days_overdue),
      })),
    [drafts],
  );
  const escalates = ladderEscalates(ladder);

  const setDraft = (index: number, patch: Partial<LevelDraft>) =>
    setDrafts((current) =>
      (current ?? []).map((draft, at) => (at === index ? { ...draft, ...patch } : draft)),
    );

  const saveLadder = () => {
    if (drafts === null) return;
    setBusy(true);
    setError(null);
    setOutcome(null);
    void replaceDunningLevels(
      drafts.map((draft) => ({
        level: Number(draft.level),
        name: draft.name.trim(),
        min_days_overdue: Number(draft.min_days_overdue),
        min_open_amount: draft.min_open_amount.trim(),
        notification_template_code: draft.notification_template_code,
        active: draft.active,
      })),
    )
      .then(async (set) => {
        setDrafts(
          [...set.items]
            .sort((left, right) => left.level - right.level)
            .map((level) => toDraft(level)),
        );
        setOutcome(
          t(
            "finance.collections.ladderSaved",
            "The ladder is now these {{count}} levels. Anything removed was deactivated, not deleted.",
            { count: set.items.filter((level) => level.active).length },
          ),
        );
        await queryClient.invalidateQueries({ queryKey: LEVELS_KEY });
      })
      .catch(() => setError(t("finance.collections.ladderError", "The ladder could not be saved.")))
      .finally(() => setBusy(false));
  };

  const sweep = () => {
    setBusy(true);
    setError(null);
    setOutcome(null);
    void runDunning(sweepAsOf === "" ? {} : { as_of: sweepAsOf }, newIdempotencyKey())
      .then((accepted) =>
        setOutcome(
          t(
            "finance.collections.sweepQueued",
            "The sweep for {{date}} is queued. An invoice already dunned at a level is not dunned again, so a rerun sends nothing.",
            { date: accepted.as_of },
          ),
        ),
      )
      .catch(() => setError(t("finance.collections.sweepError", "The sweep could not be queued.")))
      .finally(() => setBusy(false));
  };

  const sendStatements = () => {
    setBusy(true);
    setError(null);
    setOutcome(null);
    void runCustomerStatements(
      {
        currency: statementForm.currency,
        ...(statementForm.as_of === "" ? {} : { as_of: statementForm.as_of }),
      },
      newIdempotencyKey(),
    )
      .then((accepted) => setStatementRunId(accepted.id))
      .catch(() =>
        setError(t("finance.collections.statementError", "The statement run could not start.")),
      )
      .finally(() => setBusy(false));
  };

  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="space-y-1">
        <h1 className="text-xl font-semibold tracking-tight">
          {t("finance.collections.title", "Collections")}
        </h1>
        <p className="text-sm text-muted-foreground">
          {t(
            "finance.collections.subtitle",
            "How overdue invoices are chased, and the statement every customer with an open balance receives.",
          )}
        </p>
      </div>

      {error === null ? null : (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}
      {outcome === null ? null : (
        <p role="status" className="text-sm text-muted-foreground">
          {outcome}
        </p>
      )}

      <Card>
        <CardHeader>
          <CardTitle>
            <h2>{t("finance.collections.ladderTitle", "Escalation ladder")}</h2>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            {t(
              "finance.collections.ladderHint",
              "Each rung says how overdue an invoice must be, and how much must be open, before that letter goes out. Removing a rung deactivates it: a letter sent last year names a level that must stay explicable.",
            )}
          </p>

          {levels.isLoading || drafts === null ? (
            <p className="text-sm text-muted-foreground">{t("common.loading", "Loading…")}</p>
          ) : drafts.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {t("finance.collections.noLevels", "No level is configured, so nothing is chased.")}
            </p>
          ) : (
            <div className="space-y-2">
              {drafts.map((draft, index) => (
                <div
                  key={index}
                  className="grid gap-2 rounded-md border p-3 sm:grid-cols-[4rem_1fr_7rem_9rem_1fr_auto]"
                >
                  <Input
                    type="number"
                    min={1}
                    max={9}
                    dir="ltr"
                    aria-label={t("finance.collections.level", "Level")}
                    value={draft.level}
                    disabled={!canConfigure}
                    onChange={(event) => setDraft(index, { level: event.target.value })}
                  />
                  <Input
                    aria-label={t("finance.collections.name", "Name")}
                    value={draft.name}
                    disabled={!canConfigure}
                    onChange={(event) => setDraft(index, { name: event.target.value })}
                  />
                  <Input
                    type="number"
                    min={0}
                    dir="ltr"
                    aria-label={t("finance.collections.daysOverdue", "Days overdue")}
                    value={draft.min_days_overdue}
                    disabled={!canConfigure}
                    onChange={(event) => setDraft(index, { min_days_overdue: event.target.value })}
                  />
                  <Input
                    dir="ltr"
                    inputMode="decimal"
                    className="text-end font-mono"
                    aria-label={t("finance.collections.minOpen", "Least open")}
                    value={draft.min_open_amount}
                    disabled={!canConfigure}
                    onChange={(event) => setDraft(index, { min_open_amount: event.target.value })}
                  />
                  <Select
                    value={draft.notification_template_code}
                    disabled={!canConfigure}
                    onValueChange={(value) =>
                      setDraft(index, { notification_template_code: value })
                    }
                  >
                    <SelectTrigger aria-label={t("finance.collections.template", "Letter")}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {DUNNING_TEMPLATES.map((code) => (
                        <SelectItem key={code} value={code}>
                          {t(`finance.dunningTemplate.${code}`, code)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="flex items-center gap-2">
                    <label className="flex items-center gap-1 text-sm">
                      <Checkbox
                        checked={draft.active}
                        disabled={!canConfigure}
                        onCheckedChange={(checked) => setDraft(index, { active: checked === true })}
                      />
                      {t("finance.collections.active", "Active")}
                    </label>
                    {canConfigure ? (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        aria-label={t("finance.collections.removeLevel", "Remove level")}
                        onClick={() =>
                          setDrafts((current) => (current ?? []).filter((_, at) => at !== index))
                        }
                      >
                        <Trash2 className="size-4" aria-hidden="true" />
                      </Button>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          )}

          {escalates ? null : (
            <p className="text-sm text-destructive">
              {t(
                "finance.collections.notEscalating",
                "A later level waits no longer than an earlier one, so it would never be reached.",
              )}
            </p>
          )}

          {canConfigure ? (
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() =>
                  setDrafts([
                    ...(drafts ?? []),
                    {
                      level: String((drafts ?? []).length + 1),
                      name: "",
                      min_days_overdue: "0",
                      min_open_amount: "0",
                      notification_template_code: DUNNING_TEMPLATES[0],
                      active: true,
                    },
                  ])
                }
              >
                <Plus className="size-4" aria-hidden="true" />
                {t("finance.collections.addLevel", "Add a level")}
              </Button>
              <Button type="button" disabled={busy || drafts === null} onClick={saveLadder}>
                {t("finance.collections.saveLadder", "Save the ladder")}
              </Button>
            </div>
          ) : null}
        </CardContent>
      </Card>

      {canRunDunning ? (
        <Card>
          <CardHeader>
            <CardTitle>
              <h2>{t("finance.collections.sweepTitle", "Run the sweep")}</h2>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-muted-foreground">
              {t(
                "finance.collections.sweepHint",
                "The same sweep the scheduler runs nightly. An invoice already dunned at a level is not dunned again, so running it twice on one day sends nothing the second time.",
              )}
            </p>
            <div className="flex flex-wrap items-end gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="sweep-as-of">
                  {t("finance.collections.asOf", "Overdue as of")}
                </Label>
                <Input
                  id="sweep-as-of"
                  type="date"
                  dir="ltr"
                  value={sweepAsOf}
                  onChange={(event) => setSweepAsOf(event.target.value)}
                />
              </div>
              <Button type="button" disabled={busy} onClick={sweep}>
                <Send className="size-4" aria-hidden="true" />
                {t("finance.collections.sweep", "Queue the sweep")}
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : null}

      {canRunStatements ? (
        <Card>
          <CardHeader>
            <CardTitle>
              <h2>{t("finance.collections.statementsTitle", "Customer statements")}</h2>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-muted-foreground">
              {t(
                "finance.collections.statementsHint",
                "Rendering and sending happen in the background, so this reports what the run has done so far rather than claiming the statements are out.",
              )}
            </p>
            <div className="flex flex-wrap items-end gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="statement-as-of">
                  {t("finance.collections.asOf", "Overdue as of")}
                </Label>
                <Input
                  id="statement-as-of"
                  type="date"
                  dir="ltr"
                  value={statementForm.as_of}
                  onChange={(event) =>
                    setStatementForm({ ...statementForm, as_of: event.target.value })
                  }
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="statement-currency">
                  {t("finance.collections.currency", "Currency")}
                </Label>
                <Input
                  id="statement-currency"
                  dir="ltr"
                  maxLength={3}
                  className="w-24 font-mono uppercase"
                  value={statementForm.currency}
                  onChange={(event) =>
                    setStatementForm({
                      ...statementForm,
                      currency: event.target.value.toUpperCase(),
                    })
                  }
                />
              </div>
              <Button
                type="button"
                disabled={busy || statementForm.currency.length !== 3}
                onClick={sendStatements}
              >
                <Mail className="size-4" aria-hidden="true" />
                {t("finance.collections.sendStatements", "Send the statements")}
              </Button>
            </div>

            {statementRunId === null ? null : run.data === undefined ? (
              <p className="text-sm text-muted-foreground">
                {t("finance.collections.runQueued", "The run is queued.")}
              </p>
            ) : (
              <div className="flex flex-wrap items-center gap-3 rounded-md border p-3 text-sm">
                <Badge
                  variant={
                    run.data.status === "completed"
                      ? "success"
                      : run.data.status === "failed"
                        ? "destructive"
                        : "secondary"
                  }
                >
                  {t(`finance.statementRunStatus.${run.data.status}`, run.data.status)}
                </Badge>
                <span>
                  {t(
                    "finance.collections.runCounts",
                    "{{sent}} of {{parties}} sent · {{failed}} failed",
                    {
                      sent: run.data.sent_count,
                      parties: run.data.party_count,
                      failed: run.data.failed_count,
                    },
                  )}
                </span>
                <span className="text-xs text-muted-foreground" dir="ltr">
                  {run.data.as_of}
                </span>
              </div>
            )}
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}
